import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import MUIDataTable from 'mui-datatables';
import styled from 'styled-components';
import { colors } from 'sama_global/theme';

/* eslint-disable no-restricted-syntax */

const defaultOptions = {
  download: false,
  print: false,
  viewColumns: false,
  // pagination: false,
  rowsPerPage: 100,
  rowsPerPageOptions: [],
  fixedSelectColumn: false,
};

const StyledTable = styled.div`
  .MuiPaper-root {
    background-color: transparent;
    color: ${colors.light};
    box-shadow: none;

    & > div {
      overflow: visible;
    }

    .MuiSvgIcon-root {
      fill: ${colors.light};

      &:hover {
        opacity: 0.9;
      }
    }

    .MuiTableCell-body {
      color: inherit;
    }

    .MuiTableCell-root {
      border-color: ${colors.pale};
    }

    .MuiTableCell-head {
      background-color: ${colors.dark};
      color: ${colors.light};
      z-index: 200;

      span.MuiButton-label div {
        color: ${colors.light};
      }
    }

    .MuiTablePagination-root {
      color: ${colors.light};
    }
  }
`;

function Table(props) {
  const { data, columns, title, options, components, intl } = props;

  const labels = {
    body: {
      noMatch: <FormattedMessage id="table.no-match" />,
      toolTip: <FormattedMessage id="table.tooltip" />,
    },
    pagination: {
      next: <FormattedMessage id="table.next" />,
      previous: <FormattedMessage id="table.previous" />,
      rowsPerPage: <FormattedMessage id="table.rows_per_page" />,
      displayRows: intl.formatMessage({ id: 'table.display_rows' }),
      jumpToPage: <FormattedMessage id="table.jump_to_page:" />,
    },
    toolbar: {
      search: <FormattedMessage id="table.search" />,
      downloadCsv: intl.formatMessage({ id: 'table.download_csv' }),
      print: <FormattedMessage id="table.print" />,
      viewColumns: <FormattedMessage id="table.view_columns" />,
      filterTable: <FormattedMessage id="table.filter_table" />,
    },
    filter: {
      all: <FormattedMessage id="table.all" />,
      title: <FormattedMessage id="table.filter_title" />,
      reset: <FormattedMessage id="table.reset" />,
    },
    viewColumns: {
      title: <FormattedMessage id="table.title" />,
      titleAria: <FormattedMessage id="table.title_aria" />,
    },
    selectedRows: {
      text: <FormattedMessage id="table.text" />,
      delete: <FormattedMessage id="table.delete" />,
      deleteAria: <FormattedMessage id="table.delete_aria" />,
    },
  };

  useEffect(() => () => localStorage.removeItem(options.storageKey), []);

  const tableOptions = useMemo(
    () => ({
      ...defaultOptions,
      ...options,
      textLabels: labels,
    }),
    [options]
  );

  return (
    <StyledTable>
      <MUIDataTable
        data={data}
        columns={columns}
        title={title && <FormattedMessage id={title} />}
        options={tableOptions}
        components={components}
      />
    </StyledTable>
  );
}

Table.propTypes = {
  intl: PropTypes.object.isRequired,
  title: PropTypes.string,
  data: PropTypes.array,
  columns: PropTypes.array,
  options: PropTypes.object,
  components: PropTypes.object,
};

Table.defaultProps = {
  data: [],
  title: '',
  options: {},
  columns: [],
  components: {},
};

export default injectIntl(Table);
